import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2, UploadCloud } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/components/ui/accordion";
import { useToast } from "@/shared/components/ui/use-toast";
import { makeApiError } from "@/shared/utils/fetchApi";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { analyticsService } from "@/shared/services/analytics";
import { MagicFillButton } from "@/shared/components/form/MagicFillButton";
import { InputWithMagicFill } from "@/shared/components/form/InputWithMagicFill";
import { TextareaWithMagicFill } from "@/shared/components/form/TextareaWithMagicFill";
import { MagicFillProvider } from "@/shared/contexts/MagicFillContext";
import { getKnowledgeImportFormMetadata } from "@/shared/utils/magicFillMetadataHelpers";
import type { AvailableFacetForMagicFill } from "@/shared/types/magicFillTypes";
import { uploadKnowledgeFile } from "@/features/Knowledge/services/storageService";
import { useKnowledge } from "@/features/Knowledge/hooks/useKnowledge";
import { useUpload } from "@/features/Knowledge/state/UploadContext";
import { useWorkspace } from "@/features/Workspaces/state/WorkspaceContext";
import { FacetSelector } from "@/features/Facets/components/FacetSelector";
import { useFacets } from "@/features/Facets/hooks/useFacets";
import { FileUpload } from "./FileUpload";
import {
  ACCEPTED_EXTENSIONS,
  MAX_DOCUMENT_SIZE_BYTES,
  type DocumentKind,
  type DocumentRejection,
} from "@/shared/utils/documentFileTypes";
import {
  applyMagicFillSuggestions,
  buildDocumentMetadata,
  type DocumentFormFields,
} from "../utils/documentUploadForm";

interface DocumentUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploaded?: (uploadResponse: unknown) => void;
}

const METADATA_KEYS = ["source", "subject", "language", "tags"] as const;

interface FormState extends DocumentFormFields {
  file: File | null;
  kind: DocumentKind | null;
}

const EMPTY_FORM: FormState = {
  file: null,
  kind: null,
  name: "",
  description: "",
  facets: [],
  metadata: {},
};

/**
 * PBI 87080 — formulário único de envio da Storage. O botão Upload abre direto
 * aqui: o tipo é reconhecido pelo arquivo (sem etapa de escolha) e o Magic Fill
 * preenche nome, descrição, facets e metadados a partir do conteúdo.
 */
export function DocumentUploadDialog({ open, onOpenChange, onUploaded }: DocumentUploadDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { showUploadProgress, updateProgress, hideUploadProgress } = useUpload();
  const { currentWorkspace } = useWorkspace();
  const { data: knowledgeBase } = useKnowledge();
  const { data: facetRows = [], isSuccess: facetsLoaded } = useFacets(
    currentWorkspace?.organization_id,
  );
  const magicFillButtonRef = useRef<{ triggerMagicFill: () => void }>(null);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [fileError, setFileError] = useState<string | null>(null);
  const [hasTriggeredMagicFill, setHasTriggeredMagicFill] = useState(false);
  const [isMagicFilling, setIsMagicFilling] = useState(false);

  // Fechar o diálogo descarta o rascunho: reabrir começa limpo.
  useEffect(() => {
    if (!open) {
      setForm(EMPTY_FORM);
      setFileError(null);
      setHasTriggeredMagicFill(false);
      setIsMagicFilling(false);
    }
  }, [open]);

  // PBI 79319 — facetas da org mapeadas pro Magic Fill (vocabulário controlado).
  const availableFacetsForMF = useMemo<AvailableFacetForMagicFill[]>(() => {
    return facetRows.map((f) => {
      const labelKey = `facets.labels.values.${f.value}`;
      const translatedLabel = t(labelKey);
      const label = translatedLabel === labelKey ? undefined : translatedLabel;
      const parent = f.parent_facet_id
        ? facetRows.find((p) => p.id === f.parent_facet_id)
        : undefined;
      return {
        name: f.name,
        value: f.value,
        label,
        parent: parent ? { name: parent.name, value: parent.value } : null,
      };
    });
  }, [facetRows, t]);

  // Magic Fill automático depois que o arquivo é aceito. Espera o vocabulário de
  // facetas: sem ele o LLM não recebe as instruções e as facetas se perdem.
  useEffect(() => {
    if (!form.file || hasTriggeredMagicFill || !facetsLoaded) return;
    const timer = setTimeout(() => {
      magicFillButtonRef.current?.triggerMagicFill();
      setHasTriggeredMagicFill(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [form.file, hasTriggeredMagicFill, facetsLoaded]);

  const rejectionMessage = useCallback(
    (reason: DocumentRejection | "unreadable") => {
      switch (reason) {
        case "unsupported":
          return t("knowledge.imports.upload.unsupportedType", {
            type: ACCEPTED_EXTENSIONS.join(", "),
          });
        case "tooLarge":
          return t("knowledge.imports.upload.fileTooLarge", {
            size: `${MAX_DOCUMENT_SIZE_BYTES / (1024 * 1024)} MB`,
          });
        case "empty":
          return t("knowledge.imports.upload.emptyFile");
        case "corrupted":
        case "unreadable":
          return t("knowledge.imports.upload.corruptedFile");
      }
    },
    [t],
  );

  // Trocar ou remover o arquivo limpa tudo que veio do arquivo anterior — inclusive
  // o que a IA preencheu —, para o documento novo não herdar dados do antigo.
  const resetForFile = useCallback((file: File | null, kind: DocumentKind | null) => {
    setForm({ ...EMPTY_FORM, file, kind });
    setHasTriggeredMagicFill(false);
  }, []);

  const handleFileAccepted = useCallback(
    (file: File, kind: DocumentKind) => {
      setFileError(null);
      resetForFile(file, kind);
    },
    [resetForFile],
  );

  const handleFileRejected = useCallback(
    (reason: DocumentRejection) => {
      setFileError(rejectionMessage(reason));
      resetForFile(null, null);
    },
    [rejectionMessage, resetForFile],
  );

  const handleRemoveFile = useCallback(() => {
    setFileError(null);
    resetForFile(null, null);
  }, [resetForFile]);

  // O backend não conseguiu ler o conteúdo: o arquivo é recusado como corrompido.
  const handleDocumentUnreadable = useCallback(() => {
    setFileError(rejectionMessage("unreadable"));
    resetForFile(null, null);
  }, [rejectionMessage, resetForFile]);

  const getMagicFillMetadata = () => ({
    organizationId: currentWorkspace?.organization_id,
    workspaceId: currentWorkspace?.id,
    knowledgeBaseId: knowledgeBase?.id,
    fieldMetadata: getKnowledgeImportFormMetadata(availableFacetsForMF),
    // PBI 79319 — o filename e o arquivo real são sinais para a IA; o backend
    // extrai o texto do documento para todos os tipos aceitos.
    ...(form.file
      ? {
          sourceFile: {
            name: form.file.name,
            type: form.file.type,
            size: form.file.size,
            file: form.file,
          },
        }
      : {}),
  });

  const getCurrentFormValues = () => ({
    name: form.name,
    description: form.description,
    facets: form.facets,
    // PBI 79328 — metadata.* (prefixo `metadata.`) pro Magic Fill ler/escrever.
    // As quatro chaves vão sempre, vazias quando não preenchidas: o serviço só
    // pede ao LLM os campos presentes aqui, e sem elas os metadados nunca eram
    // preenchidos.
    ...Object.fromEntries(
      METADATA_KEYS.map((k) => [`metadata.${k}`, form.metadata[k] ?? ""]),
    ),
  });

  const setMetadata = (key: string, value: unknown) =>
    setForm((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        [key]: typeof value === "string" ? value : String(value ?? ""),
      },
    }));

  // O envio espera o preenchimento automático: travado do arquivo aceito até o
  // Magic Fill terminar — com sucesso, erro ou tempo limite (o botão sempre
  // libera o loading nesses três desfechos). A janela antes do disparo também
  // conta, senão dava para enviar no meio-tempo com os campos ainda vazios.
  const isAwaitingMagicFill =
    isMagicFilling || (!!form.file && facetsLoaded && !hasTriggeredMagicFill);

  const applySuggestions = (suggested: Record<string, unknown>) =>
    setForm((prev) => applyMagicFillSuggestions(prev, suggested));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isAwaitingMagicFill) return;
    const { file, kind } = form;

    if (!file || !kind) {
      setFileError(t("knowledge.imports.upload.fileRequired"));
      return;
    }
    if (!form.name.trim()) {
      toast({
        title: t("knowledge.imports.validation.requiredField"),
        description: t("knowledge.imports.validation.requiredFieldDescription"),
        variant: "destructive",
      });
      return;
    }
    // PBI 79319 — pelo menos uma faceta: o chunk já nasce classificado.
    if (form.facets.length === 0) {
      toast({
        title: t("facets.errors.atLeastOneRequiredTitle"),
        description: t("facets.errors.atLeastOneRequiredDescription"),
        variant: "destructive",
      });
      return;
    }

    const uploadStartTime = Date.now();
    try {
      if (!currentWorkspace?.id || !currentWorkspace?.organization_id) {
        throw makeApiError("Product or organization not found", { code: "E_PRODUCT_NOT_FOUND" });
      }
      if (!knowledgeBase?.id) {
        throw makeApiError("Active knowledge base not found for the current product", {
          code: "E_KNOWLEDGE_BASE_NOT_FOUND",
        });
      }

      onOpenChange(false);
      showUploadProgress(file);

      const uploadResponse = await uploadKnowledgeFile({
        file,
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        knowledgeBaseId: knowledgeBase.id,
        type: kind,
        category: "Files",
        productId: currentWorkspace.id,
        metadata: buildDocumentMetadata(form),
        onProgress: updateProgress,
      });

      await queryClient.invalidateQueries({ queryKey: ["knowledge-storage", knowledgeBase.id] });

      analyticsService.trackEvent("document_uploaded", {
        document_type: kind,
        file_size_kb: Math.round(file.size / 1024),
        upload_duration_ms: Date.now() - uploadStartTime,
        notebook_id: knowledgeBase.id,
      });
      hideUploadProgress();
      onUploaded?.(uploadResponse);
    } catch (error) {
      hideUploadProgress();
      const isSubscriptionError =
        error instanceof Error && error.message === "SUBSCRIPTION_REQUIRED";
      toast({
        title: isSubscriptionError
          ? t("knowledge.errors.subscriptionRequired")
          : t("knowledge.errors.uploadFailed"),
        description: isSubscriptionError
          ? t("knowledge.errors.subscriptionRequiredDescription")
          : resolveApiErrorMessage(error, t),
        variant: "destructive",
      });
      if (knowledgeBase?.id) {
        analyticsService.trackEvent("document_upload_failed", {
          document_type: kind,
          file_size_kb: Math.round(file.size / 1024),
          upload_duration_ms: Date.now() - uploadStartTime,
          notebook_id: knowledgeBase.id,
          error_message: error instanceof Error ? error.message : "Unknown error",
        });
      }
    }
  };

  const metadataFieldProps = (key: (typeof METADATA_KEYS)[number]) => ({
    formType: "knowledge_import" as const,
    fieldKey: `metadata.${key}`,
    getCurrentValues: getCurrentFormValues,
    onMagicFillApply: (val: unknown) => setMetadata(key, val),
    metadata: getMagicFillMetadata(),
    id: `metadata.${key}`,
    value: form.metadata[key] || "",
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setMetadata(key, e.target.value),
    placeholder: t(`knowledge.imports.metadata.${key}Placeholder`),
  });

  return (
    <MagicFillProvider>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[640px] p-0 gap-0 overflow-hidden" style={{ width: "80%" }}>
          {/* min-w-0: o DialogContent é grid, e item de grid cresce com o conteúdo —
              sem isso um nome de arquivo longo alargava o formulário inteiro. */}
          <div className="flex flex-col h-[640px] min-w-0">
            <div className="shrink-0 flex items-center gap-3 px-6 py-4 border-b">
              <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                <UploadCloud className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-lg font-semibold truncate">
                  {t("knowledge.imports.upload.title")}
                </DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground">
                  {t("knowledge.imports.upload.subtitle")}
                </DialogDescription>
              </div>
            </div>

            {/* MagicFillButton oculto — só para o disparo automático. */}
            <div className="hidden">
              <MagicFillButton
                ref={magicFillButtonRef}
                formType="knowledge_import"
                getCurrentValues={getCurrentFormValues}
                metadata={getMagicFillMetadata()}
                onApplySuggestions={applySuggestions}
                onDocumentUnreadable={handleDocumentUnreadable}
                onLoadingChange={setIsMagicFilling}
              />
            </div>

            <div className="flex-1 overflow-y-auto overflow-x-hidden">
              <form id="documentUploadForm" onSubmit={handleSubmit} className="space-y-4 p-6">
                <div className="space-y-2">
                  <Label>{t("knowledge.imports.fields.file")}</Label>
                  <FileUpload
                    value={form.file}
                    kind={form.kind}
                    error={fileError}
                    onFileAccepted={handleFileAccepted}
                    onFileRejected={handleFileRejected}
                    onRemove={handleRemoveFile}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">
                    {t("knowledge.imports.fields.name")}{" "}
                    <span className="text-destructive" aria-hidden>
                      *
                    </span>
                  </Label>
                  <InputWithMagicFill
                    formType="knowledge_import"
                    fieldKey="name"
                    getCurrentValues={getCurrentFormValues}
                    onMagicFillApply={(val) => setForm((prev) => ({ ...prev, name: String(val ?? "") }))}
                    metadata={getMagicFillMetadata()}
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder={t("knowledge.imports.fields.namePlaceholder")}
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">{t("knowledge.imports.fields.description")}</Label>
                  <TextareaWithMagicFill
                    formType="knowledge_import"
                    fieldKey="description"
                    getCurrentValues={getCurrentFormValues}
                    onMagicFillApply={(val) =>
                      setForm((prev) => ({ ...prev, description: String(val ?? "") }))
                    }
                    metadata={getMagicFillMetadata()}
                    id="description"
                    value={form.description}
                    onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder={t("knowledge.imports.fields.descriptionPlaceholder")}
                    className="h-20 w-full"
                  />
                </div>

                {/* PBI 79319 — facetas; pelo menos uma é obrigatória (validada no submit). */}
                <div className="space-y-2">
                  <Label>
                    {t("facets.title")}{" "}
                    <span className="text-destructive" aria-hidden>
                      *
                    </span>
                  </Label>
                  <FacetSelector
                    fieldKey="facets"
                    value={form.facets}
                    onChange={(next) => setForm((prev) => ({ ...prev, facets: next }))}
                  />
                </div>

                {/* PBI 79328 — metadados de negócio, recolhidos; o Magic Fill os preenche. */}
                <Accordion type="single" collapsible className="border rounded-md">
                  <AccordionItem value="metadata" className="border-b-0">
                    {/* Recolhido, o accordion esconde os campos e o loading deles:
                        o spinner no lugar da seta mostra que o Magic Fill está trabalhando. */}
                    <AccordionTrigger
                      className="px-4 text-sm"
                      loading={isMagicFilling}
                      aria-busy={isMagicFilling}
                    >
                      {t("knowledge.imports.metadata.title")}
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 space-y-3">
                      {METADATA_KEYS.map((key) => (
                        <div key={key} className="space-y-1">
                          <Label htmlFor={`metadata.${key}`}>
                            {t(`knowledge.imports.metadata.${key}`)}
                          </Label>
                          <InputWithMagicFill {...metadataFieldProps(key)} />
                        </div>
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </form>
            </div>

            <div className="shrink-0 border-t bg-accent p-4 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t("knowledge.common.cancel")}
              </Button>
              <Button
                type="submit"
                form="documentUploadForm"
                disabled={!form.file || isAwaitingMagicFill}
              >
                {isAwaitingMagicFill && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t("knowledge.imports.add")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </MagicFillProvider>
  );
}
